<template>
  <div class="layout-view">
    <div class="layout-view__controls">
      <div class="flex space-between w-full">
        <div class="header-controls-left">
          <h1 class="text-display ml-4">{{ 'base.companies' | translate }}</h1>
          <UiSearchInput 
            v-model="searchQuery"
            :placeholder="'activities.search' | translate"
            @search-input="onSearchInput"
            class="search-component"
          />
        </div>
        <div class="header-controls">
          <v-btn
            v-if="userType !== 'employee' && userType !== 'staff'"
            :disabled="isConsultant"
            medium
            @click="modals.create = true"
            class="ml-4"
          >
            <v-icon
              position-left
              font-size="1"
              icon="plus"
            />
            <span>{{ 'base.add_company' | translate }}</span>
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog
      max-width="400"
      v-model="modals.create"
    >
      <create-modal
        @close="modals.create = false"
        @update="loadData"
      />
    </v-dialog>

    <v-dialog
      max-width="400"
      :value="selected !== null"
      @input="selected = null"
    >
      <edit-modal
        :selected="selected"
        @update="loadData"
        @close="selected = null"
      />
    </v-dialog>

    <div v-if="loading" class="loading-container">
      <p>{{ 'activities.loading' | translate }}...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <p>{{ 'activities.error' | translate }}: {{ error }}</p>
      <v-btn @click="resetAndReload">{{ 'activities.try_again' | translate }}</v-btn>
    </div>

    <div v-else class="list">
      <v-card
        v-for="(item, index) in items"
        :key="item.id || index"
      >
        <div class="item flex space-between align-center">
          <div class="item-wrapp">
          <h2
            class="card-title cursor-pointer hover--underline"
            @click="onOpen(item)"
          >
            {{ item.name }}
          </h2>
          <div>{{ farmsCountLabel(item.farm_count) }}</div>
          </div>
          <v-btn
            v-if="userType !== 'employee'"
            :disabled="isConsultant"
            icon
            @click="selected = item"
          >
            <v-icon
              font-size="1.3"
              icon="cog"
            />
          </v-btn>
        </div>
      </v-card>
    </div>

    <div v-if="!loading && !error && items.length === 0" class="empty-state">
      <p v-if="searchQuery">
        {{ 'activities.search' | translate }} "{{ searchQuery }}" {{ 'activities.not_found' | translate }}
      </p>
      <p v-else>{{ 'base.no_companies' | translate }}</p>
    </div>

    <ui-pagination
      v-if="showPagination && !searchQuery"
      v-model="page"
      :page-count="pageCount"
      @page-change="pageChangeHandler"
    />
  </div>
</template>

<script>
import api from '@/api'

import CreateModal from './modals/CreateModal'
import EditModal from './modals/EditModal'
import UiSearchInput from '@/components/ui/ui-search-input/UiSearchInput'

import UiPagination from '@/components/ui/ui-pagination/UiPagination'

import paginationMixin from '@/mixins/pagination.mixin'

export default {
  name: 'CompaniesList',

  mixins: [paginationMixin],

  components: {
    CreateModal,
    EditModal,
    UiPagination,
    UiSearchInput
  },

  data: () => ({
    userType: null,
    modals: {
      create: false
    },
    selected: null,
    searchQuery: '',
    loading: false,
    error: null,
    searchTimeout: null,
    isSearching: false
  }),

  computed: {
    isConsultant () {
      return this.userType === 'consultant'
    }
  },

  watch: {
    searchQuery (newValue) {
      if (!newValue || !newValue.trim()) {
        this.isSearching = false
        this.loadData()
      }
    }
  },

  async created () {
    try {
      const { data } = await api.account.get()
      this.userType = data.user_type
    } catch (error) {
      console.error('Failed to fetch user type:', error)
      this.userType = null
    }
  },

  mounted () {
    this.initializeData()
  },

  beforeDestroy () {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    farmsCountLabel (number) {
      const cases = [2, 0, 1, 1, 1, 2]
      const keyIndex = (number % 100 > 4 && number % 100 < 20)
        ? 2
        : cases[(number % 10 < 5) ? number % 10 : 5]

      const labels = [
        this.$t('activities.farm_one'),
        this.$t('activities.farm_few'),
        this.$t('activities.farm_many')
      ]

      return `${number} ${labels[keyIndex]}`
    },

    onOpen (item) {
      const KEY = `company_${item.id}_name`
      sessionStorage.setItem(KEY, item.name)
      this.$router.push(`${this.$route.path}/${item.id}/farms`)
    },

    onSearchInput () {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.performSearch()
      }, 300)
    },

    async performSearch () {
      try {
        this.loading = true
        this.error = null
        this.isSearching = true

        if (this.searchQuery && this.searchQuery.trim()) {
          const params = {
            name: this.searchQuery.trim()
          }
          const response = await api.companies.list(params)

          if (response.data) {
            this.items = response.data.companies || response.data.data || response.data || []
          } else {
            this.items = response || []
          }
        } else {
          this.isSearching = false
          this.initializeData()
          return
        }
      } catch (error) {
        console.error('Search error:', error)
        this.error = error.message || this.$t('activities.search_error')
        this.items = []
      } finally {
        this.loading = false
      }
    },

    initializeData () {
      this.isSearching = false
      this.searchQuery = ''
      this.setupPagination(
        api.companies.list,
        null,
        'companies'
      )
    },

    resetAndReload () {
      this.searchQuery = ''
      this.error = null
      this.isSearching = false
      this.initializeData()
    },
    onUpdate () {
      if (!this.isSearching) {
        this.initializeData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_mixins.scss';

.layout-view__controls {
  margin-bottom: 2rem;
  
  .flex {
    align-items: center;
  }
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  justify-content: flex-end;
  max-width: 500px;
}
.header-controls-left {
  display: flex;
  align-items: center;
  gap: 4rem;
  width: 50%;
}
.search-component {
  flex: 1;
  max-width: 320px;
}

.loading-container,
.error-container,
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
}

.error-container {
  color: #d32f2f;
}

.empty-state {
  background: #f5f5f5;
  border-radius: 1rem;
  margin: 2rem 0;
}

.list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: 1.5rem;
  row-gap: 1.5rem;

  @include respond(lg) {
    grid-template-columns: 1fr 1fr;
  }

  @include respond(sm) {
    grid-template-columns: 1fr;
  }
}

.item-wrapp {
  display: flex;
  flex-direction: column;
}

.item {
  padding: 1.4rem 0.6rem 1.4rem 1.6rem;
  border-radius: 1rem;
  background: #fff;
  transition: 0.3s all;
  
  &:hover {
    background: #D1E6D7;
  }
  
  h2 {
    margin-bottom: 1rem;
    font-size: 17px;
    font-weight: 900;
  }
  
  div {
    font-size: 15px;
  }
}

@media (max-width: 768px) {
  .layout-view__controls .flex {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-controls {
    justify-content: space-between;
    max-width: none;
  }
  
  .search-component {
    max-width: none;
  }
}
</style>